import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ThemeToggle from './ThemeToggle';

interface MainLayoutProps {
  children: React.ReactNode;
  title?: string;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children, title }) => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Update document title
  useEffect(() => {
    document.title = title ? `${title} | Triple Point` : 'Triple Point';
  }, [title]);
  
  // Close mobile menu when location changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);
  
  const navigation = [
    { name: 'Dashboard', href: '/' },
    { name: 'Strategy Configuration', href: '/config' },
    { name: 'Stock Analysis', href: '/stocks' },
    { name: 'Portfolio', href: '/portfolio' },
    { name: 'Reports', href: '/reports' },
    { name: 'About', href: '/about' },
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <nav className="bg-primary-800 dark:bg-gray-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              {/* Logo - Clickable and links to home page */}
              <Link to="/" className="flex-shrink-0 flex items-center group">
                <img 
                  src={`${process.env.PUBLIC_URL}/triple-point-favicon.svg`}
                  alt="Triple Point Logo" 
                  className="h-8 w-8 mr-2 transition-transform group-hover:scale-110" 
                />
                <span className="text-white text-xl font-bold group-hover:text-primary-300 transition-colors">Triple Point</span>
              </Link>
            </div>
            
            <div className="flex items-center">
              <ThemeToggle />
              
              {/* Hamburger menu button */}
              <button 
                className="ml-4 text-gray-300 hover:text-white focus:outline-none"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                <svg 
                  className="h-6 w-6" 
                  xmlns="http://www.w3.org/2000/svg" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  {mobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>
              </div>
        
        {/* Mobile navigation menu */}
        <div className={`${mobileMenuOpen ? 'block' : 'hidden'} transition-all duration-300 ease-in-out`}>
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 border-t border-primary-700 dark:border-gray-700">
                  {navigation.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`${
                        location.pathname === item.href
                          ? 'bg-primary-900 dark:bg-gray-700 text-white'
                          : 'text-gray-300 hover:bg-primary-700 dark:hover:bg-gray-600 hover:text-white'
                } block px-3 py-2 rounded-md text-base font-medium`}
                    >
                      {item.name}
                    </Link>
                  ))}
          </div>
        </div>
      </nav>

      <main className="flex-grow bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          {children}
        </div>
      </main>

      <footer className="bg-primary-800 dark:bg-gray-800 text-white py-4 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-sm mb-4 md:mb-0">
              &copy; {new Date().getFullYear()} Triple Point Trading Strategy. All rights reserved.
            </div>
            <div>
              <Link to="/about#disclaimer" className="text-gray-300 hover:text-white text-sm font-medium">
                Financial Disclaimer
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MainLayout; 